import { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Zap, Search, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
  Line,
  Cell,
} from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
};

const formatCurrencySigned = (value) => {
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
  return value < 0 ? `-${formatted}` : formatted;
};

export default function QuickCheck() {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (error) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const selectCategory = async (category) => {
    setSelectedCategory(category);
    try {
      const res = await api.get(`/quick-check/${category.id}?year=2025`);
      setCategoryData(res.data);
    } catch (error) {
      toast.error("Failed to load category data");
    }
  };

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  // Group by category group
  const groupedCategories = {};
  filteredCategories.forEach((cat) => {
    if (!groupedCategories[cat.group]) groupedCategories[cat.group] = [];
    groupedCategories[cat.group].push(cat);
  });

  // Calculate monthly budget for reference
  const monthlyBudget = categoryData ? (categoryData.annual_budget / 12) : 0;
  
  // Calculate actual remaining (negative when overspent)
  const remaining = categoryData ? categoryData.remaining : 0;
  const isOverspent = remaining < 0;
  
  // Percentage spent
  const percentSpent = categoryData && categoryData.annual_budget > 0 
    ? (Math.abs(categoryData.ytd_spent) / categoryData.annual_budget) * 100 
    : 0;

  // Monthly budget remaining (should be negative if overspent)
  const monthsRemaining = 12 - new Date().getMonth();
  const monthlyBudgetLeft = remaining / Math.max(1, monthsRemaining);
  
  // Find the month where cumulative spending crossed the budget
  const findCrossoverMonth = () => {
    if (!categoryData || !isOverspent) return null;
    let cumulative = 0;
    const annualBudget = categoryData.annual_budget;
    for (let idx = 0; idx < 12; idx++) {
      cumulative += Math.abs(categoryData.monthly_breakdown[idx] || 0);
      if (cumulative > annualBudget) {
        return idx; // Return the index of the month that crossed over
      }
    }
    return null;
  };
  
  const crossoverMonthIndex = findCrossoverMonth();
  
  // Chart data for selected category with budget reference
  const chartData = categoryData
    ? MONTHS.map((month, idx) => ({
        name: month,
        amount: Math.abs(categoryData.monthly_breakdown[idx] || 0),
        budget: monthlyBudget,
        isCrossover: idx === crossoverMonthIndex,
      }))
    : [];

  return (
    <div className="space-y-6 animate-slide-in-up">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight text-slate-900">
          Quick Check
        </h1>
        <p className="text-slate-500 mt-1">
          Quickly see what's left in any budget category
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Category List */}
        <Card className="lg:col-span-4 border-slate-200">
          <CardHeader className="pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search categories..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="category-search"
              />
            </div>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {["Needs", "Wants", "Bills", "Income"].map((group) => {
                  const cats = groupedCategories[group] || [];
                  if (cats.length === 0) return null;

                  return (
                    <div key={group}>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                        {group}
                      </p>
                      <div className="space-y-1">
                        {cats.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => selectCategory(cat)}
                            className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                              selectedCategory?.id === cat.id
                                ? "bg-sky-50 text-sky-700 border border-sky-200"
                                : "hover:bg-slate-50"
                            }`}
                            data-testid={`category-item-${cat.id}`}
                          >
                            <span className="flex items-center gap-2">
                              {cat.emoji && <span>{cat.emoji}</span>}
                              <span className="font-medium">{cat.name}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Details */}
        <div className="lg:col-span-8 space-y-6">
          {categoryData ? (
            <>
              {/* Summary Card - Remaining is now most prominent */}
              <Card className="border-slate-200 quick-check-card">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-300 text-sm font-medium">
                        {categoryData.category.group}
                      </p>
                      <h2 className="text-2xl font-bold font-['Outfit'] mt-1 flex items-center gap-2">
                        {categoryData.category.emoji && (
                          <span>{categoryData.category.emoji}</span>
                        )}
                        {categoryData.category.name}
                      </h2>
                    </div>
                    <Zap className="h-8 w-8 text-sky-400" strokeWidth={1.5} />
                  </div>

                  {/* Remaining is now FIRST and LARGEST */}
                  <div className="mt-6 mb-4">
                    <p className="text-slate-400 text-sm">Remaining Budget</p>
                    <p className={`text-4xl font-bold font-mono mt-1 ${
                      remaining >= 0 ? "text-emerald-400" : "text-rose-400"
                    }`}>
                      {formatCurrencySigned(remaining)}
                    </p>
                    {isOverspent && (
                      <p className="text-rose-400 text-sm flex items-center gap-1 mt-1">
                        <AlertTriangle className="h-4 w-4" />
                        Over budget by {formatCurrency(Math.abs(remaining))}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-slate-400 text-sm">Annual Budget</p>
                      <p className="text-xl font-bold font-mono mt-1">
                        {formatCurrency(categoryData.annual_budget)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">YTD Spent</p>
                      <p className="text-xl font-bold font-mono mt-1">
                        {formatCurrency(categoryData.ytd_spent)}
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar - handles >100% properly */}
                  <div className="mt-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className={`font-medium ${isOverspent ? "text-rose-400" : "text-slate-400"}`}>
                        {percentSpent.toFixed(0)}% {isOverspent ? "over budget" : "used"}
                      </span>
                      <span className="text-slate-400">
                        {formatCurrency(categoryData.annual_budget)} total
                      </span>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden relative">
                      {/* Base bar up to 100% */}
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          isOverspent ? "bg-rose-400" : "bg-sky-400"
                        }`}
                        style={{
                          width: `${Math.min(percentSpent, 100)}%`,
                        }}
                      />
                      {/* Overage indicator for >100% */}
                      {isOverspent && (
                        <div className="absolute right-0 top-0 h-full flex items-center px-2">
                          <span className="text-[10px] font-bold text-rose-300">
                            +{(percentSpent - 100).toFixed(0)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <TrendingDown className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Avg Monthly Spent</p>
                        <p className="text-xl font-bold font-mono">
                          {formatCurrency(categoryData.avg_monthly_spent)}
                        </p>
                        <p className="text-xs text-slate-400">
                          vs {formatCurrency(monthlyBudget)}/mo budget
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${monthlyBudgetLeft >= 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
                        {monthlyBudgetLeft >= 0 ? (
                          <TrendingUp className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-rose-600" strokeWidth={1.5} />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Monthly Budget Left</p>
                        <p className={`text-xl font-bold font-mono ${
                          monthlyBudgetLeft >= 0 ? "text-emerald-600" : "text-rose-600"
                        }`}>
                          {formatCurrencySigned(monthlyBudgetLeft)}
                        </p>
                        <p className="text-xs text-slate-400">
                          {monthsRemaining} months remaining
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Chart with Budget Reference */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-['Outfit'] text-lg">
                      Monthly Spending vs Budget
                    </CardTitle>
                    {/* Legend */}
                    <div className="flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-sky-400 rounded-sm"></div>
                        <span className="text-slate-500">Monthly Spent</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-0.5 bg-amber-500" style={{borderStyle: 'dashed', borderWidth: '1px 0 0 0', borderColor: '#F59E0B'}}></div>
                        <span className="text-slate-500">Monthly Budget ({formatCurrency(monthlyBudget)})</span>
                      </div>
                      {crossoverMonthIndex !== null && (
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="h-3 w-3 text-rose-500" />
                          <span className="text-rose-500">Budget Exceeded</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(v) => `$${v}`} />
                        <Tooltip
                          formatter={(value, name) => [
                            formatCurrency(value), 
                            name === "amount" ? "Spent" : "Budget"
                          ]}
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #E2E8F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Bar dataKey="amount" radius={[4, 4, 0, 0]} name="Spent">
                          {chartData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.isCrossover ? "#F43F5E" : "#38BDF8"} 
                            />
                          ))}
                        </Bar>
                        <ReferenceLine 
                          y={monthlyBudget} 
                          stroke="#F59E0B" 
                          strokeDasharray="5 5" 
                          strokeWidth={2}
                        />
                        {/* Crossover month annotation */}
                        {crossoverMonthIndex !== null && (
                          <ReferenceLine
                            x={MONTHS[crossoverMonthIndex]}
                            stroke="#F43F5E"
                            strokeWidth={0}
                            label={{
                              value: "⚠️ Over Budget",
                              position: "top",
                              fill: "#F43F5E",
                              fontSize: 10,
                              fontWeight: "bold"
                            }}
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Zap className="h-12 w-12 text-slate-300 mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  Select a category
                </h3>
                <p className="text-slate-500 text-center max-w-sm">
                  Click on any category from the list to quickly see how much budget you have left
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
